import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import createDOMPurify from 'dompurify';
import { Button, ButtonVariant, ButtonSize } from '../atoms/button';
import useMavenApi, { MavenApiError } from '../../auth/useMavenApi';
import LogoLoader from '../LogoLoader';

/**
 * The written consultation from imavenai.com, read on the client platform.
 *
 * GET /client/consultations/:engagementId/advice → { status, html, summary, disclaimer, advisorName, createdAt }.
 * The HTML is Maven's own report (inline styles + SVG charts, designed for a white page, the same
 * markup the site renders in advice-view.tsx), so it sits on a white sheet inside the modal.
 *
 * It is still sanitized here: this app holds the client's session, so we do not inject server HTML
 * unchecked. A private DOMPurify instance is used because ChatMessage.jsx calls the global
 * DOMPurify.setConfig() with a chat-only tag list, which would strip the report's tables and charts.
 */
const purifier = createDOMPurify(window);
const sanitizeReport = (html) => purifier.sanitize(String(html || ''), {
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
  ADD_ATTR: ['target'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select'],
});

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(23, 36, 62, 0.35);
  z-index: 3000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 60px 8px 80px;
  }
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 880px;
  background: linear-gradient(145deg, #ffffff 0%, #f8faff 100%);
  border: 1px solid #dce5f4;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(39, 68, 131, 0.14);
  color: #192947;
  overflow: hidden;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #e0e8f5;

  .title { font-weight: 600; font-size: 1.05em; }
  .sub { font-size: 0.8em; color: #7b8aa3; margin-top: 2px; }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const Sheet = styled.div`
  background: #ffffff;
  color: #192947;
  margin: 16px;
  border-radius: 10px;
  padding: 24px;
  line-height: 1.6;
  font-size: 15px;
  overflow-x: auto;

  img, svg { max-width: 100%; height: auto; }
  table { max-width: 100%; }
  a { color: #2d5bed; }

  @media (max-width: 768px) {
    margin: 8px;
    padding: 14px;
  }
`;

const Disclaimer = styled.p`
  margin: 0 16px 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f5f8fd;
  border: 1px solid #e0e8f5;
  font-size: 12px;
  line-height: 1.6;
  color: #65748d;
`;

const State = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #455776;
`;

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const escapeText = (s) => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const ConsultationAdviceModal = ({ engagementId, advisorName: fallbackName, onClose }) => {
  const callMaven = useMavenApi();
  const [state, setState] = useState({ loading: true, error: null, advice: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, advice: null });
    callMaven(`/client/consultations/${encodeURIComponent(engagementId)}/advice`)
      .then((advice) => { if (!cancelled) setState({ loading: false, error: null, advice }); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof MavenApiError && e.status === 404
          ? 'This consultation is not available for your account.'
          : (e?.message || 'Could not load the consultation.');
        setState({ loading: false, error: msg, advice: null });
      });
    return () => { cancelled = true; };
  }, [engagementId, callMaven]);

  // Escape closes, like a click on the backdrop.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const advice = state.advice;
  const safeHtml = useMemo(() => (advice?.html ? sanitizeReport(advice.html) : ''), [advice]);
  const name = advice?.advisorName || fallbackName || 'Your AI advisor';
  const ready = advice && advice.status === 'ready' && safeHtml;

  // Print the report on its own page (window.print() here would print the whole app shell).
  const print = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeText(name)} — consultation</title>
      <style>body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#192947;max-width:860px;margin:24px auto;padding:0 16px;line-height:1.6}
      .disclaimer{margin-top:24px;padding:12px;background:#f5f8fd;color:#65748d;font-size:12px;border-radius:8px}</style></head>
      <body>${safeHtml}${advice?.disclaimer ? `<p class="disclaimer">${escapeText(advice.disclaimer)}</p>` : ''}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label={`Consultation from ${name}`}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <div>
            <div className="title">{name}</div>
            {advice?.createdAt && <div className="sub">Written consultation · {formatDate(advice.createdAt)}</div>}
          </div>
          <HeaderActions>
            {ready && <Button variant={ButtonVariant.SCHEDULE_OUTLINE} size={ButtonSize.SM} onClick={print}>Print / PDF</Button>}
            <Button variant={ButtonVariant.SCHEDULE} size={ButtonSize.SM} onClick={onClose}>Close</Button>
          </HeaderActions>
        </DialogHeader>

        {state.loading && <State><LogoLoader size={150} message="Loading the consultation" /></State>}
        {!state.loading && state.error && <State role="alert">{state.error}</State>}
        {!state.loading && advice && !ready && (
          <State>
            {advice.status === 'generating'
              ? 'The consultation is still being written. Check again in a minute.'
              : 'This consultation is not ready to read yet.'}
          </State>
        )}
        {ready && (
          <>
            {/* Sanitized above with a private DOMPurify instance (html + svg profiles). */}
            <Sheet dangerouslySetInnerHTML={{ __html: safeHtml }} />
            {advice.disclaimer && <Disclaimer>{advice.disclaimer}</Disclaimer>}
          </>
        )}
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default ConsultationAdviceModal;
